import { useStore } from "@xyflow/react";
import { useEffect } from "react";

import { gridFadeForZoom } from "../hooks/useCanvasCursorGlow";

/**
 * Fades the rendered grid out as the camera zooms away — the design
 * system's canvas ground behavior (`showcase/canvas.css` `--cv-grid-fade`):
 * fully visible at ≥50% zoom, gone at ≤30%, linear in between. Every
 * visible grid line is a real snap target (lattice ruling A3), so the
 * grid disappears when its pitch becomes too dense to mean anything
 * rather than shimmering on.
 *
 * Renders nothing; writes an inline `opacity` on the children of every
 * `<Background />` SVG (the pattern + the rect that paints it — never the
 * SVG itself, which carries the ground color). The write goes to the
 * consuming elements rather than a custom property on the React Flow root:
 * custom properties inherit, so a per-tick write on the root would dirty
 * style for every node on the canvas.
 *
 * Mount it only while the grid is shown, so a grid toggle remounts it and
 * the effect re-runs against the fresh SVGs.
 */
export function CanvasGridFade() {
  const zoom = useStore((s) => s.transform[2]);
  const domNode = useStore((s) => s.domNode);

  useEffect(() => {
    if (!domNode) return;
    const apply = () => {
      const opacity = String(gridFadeForZoom(zoom));
      domNode
        .querySelectorAll<SVGElement>(".react-flow__background > *")
        .forEach((child) => {
          child.style.opacity = opacity;
        });
    };
    apply();
    // A grid-mode swap (dots ↔ lines) replaces the SVG children without a
    // zoom change; re-apply so the fresh pattern is not painted at full
    // opacity until the next zoom tick.
    const observer = new MutationObserver(apply);
    domNode.querySelectorAll(".react-flow__background").forEach((svg) => {
      observer.observe(svg, { childList: true });
    });
    return () => observer.disconnect();
  }, [domNode, zoom]);

  return null;
}
